import { v4 as uuid } from 'uuid';

import { sortByWeight } from '@techchange/dnd';
import { toSlug, SOCIAL_MEDIA_VALIDATION } from '../../_shared';
import { messages } from '../../_shared/i18n/messages';

import { CONTINUATION_TYPES, FIELD_TYPES } from '../constants';

export * from './propTypes';
export * from './validation';

export const getWidthOptions = (intl) => {

	return [
		{
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_WIDTH_OPTION_FULL),
			val: 12,
		},
		{
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_WIDTH_OPTION_THREE_FOURTHS),
			val: 9,
		},
		{
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_WIDTH_OPTION_TWO_THIRDS),
			val: 8,
		},
		{
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_WIDTH_OPTION_HALF),
			val: 6,
		},
		{
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_WIDTH_OPTION_ONE_THIRD),
			val: 4,
		},
		{
			label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_WIDTH_OPTION_ONE_FOURTH),
			val: 3,
		}
	];

};

/**
 * Finds and returns the index for the parent section
 * of a form field and its index within the list of sibling fields
 * in the section.
 *
 * @param {Array}  sections  The list of form sections with nested fields.
 * @param {String} fieldID   The ID of the form field to find.
 *
 * @returns {Array} result
 * @returns {Integer} result[0]  The index of the parent form section.
 * @returns {Integer} result[1]  The index of the form field within the list of sibling fields.
 */
export const findFormFieldIndices = (sections, fieldID) => {

	// Find the field from the nested state
	let originalSectionIdx, originalFieldIdx;

	for (let i = 0; i < sections.length; i++) {

		const section = sections[i];

		for (let j = 0; j < section.fields.length; j++) {

			const field = section.fields[j];

			if (fieldID === field.id) {
				originalSectionIdx = i;
				originalFieldIdx = j;
				break;
			}

		}

	}

	return [originalSectionIdx, originalFieldIdx];
};

/**
 * Finds and returns the index for the parent section
 * of a form setting and its index within the list of sibling settings.
 *
 * @param {Array}  sections   The list of form sections with nested settings.
 * @param {String} settingID  The ID of the advanced setting to find.
 *
 * @returns {Array} result
 * @returns {Integer} result[0]  The index of the parent form section.
 * @returns {Integer} result[1]  The index of the advanced setting within the
 *                               list of sibling settings.
 */
export const findAdvancedSettingIndices = (sections, settingID) => {

	// Find the setting from the nested state
	let originalSectionIdx, originalSettingIdx;

	for (let i = 0; i < sections.length; i++) {

		const section = sections[i];

		for (let j = 0; j < section.advanced_settings.length; j++) {

			const setting = section.advanced_settings[j];

			if (settingID === setting.id) {
				originalSectionIdx = i;
				originalSettingIdx = j;
				break;
			}

		}

	}

	return [originalSectionIdx, originalSettingIdx];
};

/**
 * Initialize modal data by populating all existing tickets/ticket groups with
 * a continuation setting. Check advanced settings to find if a setting already
 * exists and use that setting. Otherwise, set the continuation type to default.
 *
 * @param {String} sectionID     The section ID.
 * @param {Array}  settings      The list of existing advanced navigation settings for the section.
 * @param {Array}  tickets       The list of tickets for which to support navigation settings.
 * @param {Array}  ticketGroups  The list of ticket groups for which to support navigation settings.
 *
 * @returns {Array} The list of default advanced settings for the section.
 */
export const getAdvancedSettings = (sectionID, settings, tickets, ticketGroups) => {

	// Make sure tickets are sorted by weight
	const sortedTickets = sortByWeight(tickets);

	// Move inactive tickets to end of the list
	sortedTickets.sort((ticket1, ticket2) => ticket2.active - ticket1.active);

	// Make sure ticket groups are sorted by weight
	const sortedTicketGroups = sortByWeight(ticketGroups);

	const getSetting = (objectType, object) => {

		// Check if there is an existing setting for this ticket
		const existingSetting =
			settings.find((setting) => setting.object_id === object.id &&
				setting.form_section_id === sectionID);

		// If there's not an existing setting, use the default
		if (!existingSetting) {
			return {
				id: uuid(),
				object_type: objectType,
				object_id: object.id,
				continuation_type: CONTINUATION_TYPES.DEFAULT,
				continuation_section_id: null,
				form_section_id: sectionID,
				// Include title to display in UI
				title: object.title,
				// Include active flag (product tickets only) to display in UI
				active: Boolean(object.active),
			};
		}

		// Otherwise, return the existing setting
		return {
			...existingSetting,
			// Include title to display in UI
			title: object.title,
			// Include active flag (product tickets only) to display in UI
			active: Boolean(object.active),
		};

	};

	// Build out settings objects for tickets
	const ticketAdvancedSettings = sortedTickets.map((ticket) =>
		getSetting(CONTINUATION_TYPES.PRODUCT_TICKETS, ticket));

	// Build out settings objects for ticket groups
	const ticketGroupAdvancedSettings = sortedTicketGroups.map((ticketGroup) =>
		getSetting(CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS, ticketGroup));

	return [...ticketAdvancedSettings, ...ticketGroupAdvancedSettings];

};

/**
 * Converts the form entry to form data.
 *
 * @param {object} form     The form.
 * @param {object} entry    The form entry (if any).
 * @param {object} options  The options for the conversion.
 *
 * @returns {object} data   The form data, i.e. a map of form field ID -> value.
 */
export const convertFormEntryToData = (form, entry, options = { prefill: true }) => {

	const data = {};

	const formFields = form?.form_fields || [];
	const formValues = entry?.form_values || [];

	// Check if we need to prefill the data for the form and form fields
	if (options.prefill) {

		// Loop through each of the form fields and initialize a blank value
		for (let i = 0, len = formFields.length; i < len; i++) {

			const field = formFields[i];
			const type = field.type;

			// Set a default value for the input
			data[field.id] = '';

			// Set a default privacy value for the input
			data[`${field.id}:privacy`] = null;

			// Set a default meta value for the input
			data[`${field.id}:meta`] = null;

			// For checkbox inputs, initialize the value to false
			if (type === FIELD_TYPES.CHECKBOX) {

				data[field.id] = false;

			// For multiple checkbox inputs, initialize an empty array of values
			} else if (type === FIELD_TYPES.CHECKBOXES) {

				data[field.id] = [];

			// For multiple choice, dropdown inputs, select a predefined default value
			// if there is no placeholder option already...
			} else if (type === FIELD_TYPES.SELECT && !field.placeholder) {

				// Sort the options
				const sortedOptions = sortByWeight(field.form_field_options);

				// Prefill the default option in the dropdown input
				if (sortedOptions.length > 0) {

					data[field.id] = sortedOptions[0].value;

					// Keep track of the selected option ID for conditional navigation logic
					data[`${field.id}:selectedOptionID`] = sortedOptions[0].id;

				}

			}

		}

	}

	const fieldsByID = {};

	formFields.forEach((field) => { fieldsByID[field.id] = field; });

	// Loop through each of the values for the form and update the initial values
	for (let i = 0, len = formValues.length; i < len; i++) {

		const value = formValues[i];
		const fieldID = value.form_field_id;

		const field = fieldsByID[fieldID];

		// Update the initial data value for the form field
		if (value.data) {

			const type = field.type;

			const optionsByID = {};

			field.form_field_options?.forEach((option) => { optionsByID[option.id] = option; });

			// Check if this form value has selected form field options
			if (value.selected_form_field_options &&
				value.selected_form_field_options.length > 0) {

				// Check if the form field supports selecting only a single option
				if (type === FIELD_TYPES.RADIO || type === FIELD_TYPES.SELECT) {

					// Use the selected option to find the up-to-date data value

					const selectedOptionId = value.selected_form_field_options[0].id;

					const selectedOption = optionsByID[selectedOptionId];

					data[fieldID] = selectedOption?.value || null;

					// Keep track of the selected option ID for conditional navigation logic
					data[`${fieldID}:selectedOptionID`] = selectedOption?.id;

				// Otherwise, check if it supports selecting multiple options
				} else if (type === FIELD_TYPES.CHECKBOXES) {

					// Use an array of the selected options to build up-to-date data value

					const selectedOptions =
						value.selected_form_field_options
							.map((option) => optionsByID[option.id])
							.filter((option) => Boolean(option))
							.map((option) => option.value);

					data[fieldID] = selectedOptions;

				// Otherwise, check if it supports selecting a single likert option
				} else if (type === FIELD_TYPES.LIKERT) {

					// Use the selected option to find the up-to-date data value

					const selectedOptionId = value.selected_form_field_options[0].id;

					const selectedOption = optionsByID[selectedOptionId];

					data[fieldID] = selectedOption ? parseInt(selectedOption.value, 10) : null;

				}

			// Otherwise, use the initial data value as is
			} else {

				data[fieldID] = value.data;

			}

		}

		// Update the initial privacy level for the form field
		data[`${fieldID}:privacy`] = value.privacy;

		// Update the initial metadata for the form field
		data[`${fieldID}:meta`] = value.meta;

	}

	return data;

};

/**
 * Converts the form data to an up-to-date form entry.
 *
 * @param {object}  form                      The form.
 * @param {object}  entry                     The existing form entry (if any).
 * @param {object}  data                      The form data, i.e. a map of
 *                                            `${fieldID}` -> data value and
 *                                            `${fieldID}:privacy` -> privacy value.
 * @param {object}  options                   Parsing options.
 * @param {string}  options.formSectionID     The form section ID to filter fields by
 * @param {boolean} options.bypassValidation  Flag indicating if field validation should be skipped
 * @param {string} options.preferredLanguage  The preferred language for the current user.
 * @param {string} options.defaultLanguage    The default language for the form.
 *
 * @returns {array}  result
 * @returns {object} result[0]  The form entry.
 * @returns {object} result[1]  The errors related to the form entry (if any).
 */
export const convertDataToFormEntry = (form, entry, data = {}, options) => {

	const formSectionID = options.formSectionID || null;
	const bypassValidation = options.bypassValidation || false;
	const preferredLanguage = options.preferredLanguage;
	const defaultLanguage = options.defaultLanguage;

	let formFields = form.form_fields || [];

	if (formSectionID) {
		formFields = formFields.filter((field) => field.form_section_id === formSectionID);
	}

	// Keep track of the up-to-date form values for the form entry
	const formValues = [];

	// Keep track of any errors related to the form fields and values
	let errors = {};

	// Loop through the form fields to create/update the form values
	for (let i = 0, len = formFields.length; i < len; i++) {

		const field = formFields[i];
		const type = field.type;

		// Build the value for the form entry for this field
		const value = {
			data: data[field.id],
			privacy: data[`${field.id}:privacy`],
			meta: data[`${field.id}:meta`],
			form_field_id: field.id,
			selected_form_field_options: [],
		};

		// Check if this field requires selecting a single form field option
		if (type === FIELD_TYPES.RADIO ||
			type === FIELD_TYPES.SELECT) {

			// Get the matching form field option based on the selected option value
			const selectedOption =
				field.form_field_options?.find((option) => option.value === value.data);

			if (selectedOption) {
				value.selected_form_field_options.push(selectedOption.id);
			}

		// Otherwise, check if this field requires selecting multiple form field options
		} else if (type === FIELD_TYPES.CHECKBOXES) {

			const selectedOptionValues = data[field.id];

			// eslint-disable-next-line
			for (let j = 0, jLen = selectedOptionValues.length; j < jLen; j++) {

				const selectedValue = selectedOptionValues[j];

				// Get the matching form field option based on the selected option value
				const selectedOption =
					field.form_field_options?.find((option) => option.value === selectedValue);

				if (selectedOption) {
					value.selected_form_field_options.push(selectedOption.id);
				}

			}

		// Otherwise, check if this field requires selecting a single likert option
		} else if (type === FIELD_TYPES.LIKERT) {

			// Get the matching form field option based on the selected option value
			const selectedOption = field.form_field_options?.find(
				(option) => parseInt(option.value, 10) === parseInt(value.data, 10)
			);

			if (selectedOption) {
				value.selected_form_field_options.push(selectedOption.id);
			}

		}

		// Check if we need to update an already existing form value
		const existingValue = entry?.form_values?.find((val) => val.form_field_id === field.id);

		// If so, preserve the existing ID of the form value
		if (existingValue) {
			value.id = existingValue.id;
		}

		// Check if there are any errors related to this form field and value
		errors = Object.assign({}, errors, validateFormField(field, value.data));

		formValues.push(value);

	}

	const formEntry = {
		language: preferredLanguage,
		form_id: form.id,
		scope: form.scope,
		scope_id: form.scope_id,
		form_values: formValues,
	};

	// Check if updating the values of an existing form entry
	if (entry) {
		formEntry.id = entry.id;
	}

	// If no translations exist for the preferred language, fallback to the default
	if (missingFormTranslations(form, preferredLanguage)) {
		formEntry.language = defaultLanguage;
	}

	// Skip form validation if bypass option set
	if (bypassValidation) {
		return [formEntry, {}];
	}

	return [formEntry, errors];

};

/**
 * Builds the ordered list of form sections with nested field data
 * given the form fields, sections, and advanced settings.
 *
 * @param {Object} form                The form.
 * @param {Array} form.form_sections  The list of form sections.
 * @param {Array} form.form_fields    The list of form fields.
 * @param {Array} [form.form_section_advanced_settings]  The list of form section advanced settings.
 *
 * @param {Object} options                  Function options.
 * @param {String} options.defaultLanguage  The default language for the form.
 * @param {Array}  options.tickets          The list of tickets used to build out
 *                                           default advanced navigation settings.
 * @param {Array}  options.ticketGroups     The list of ticket groups used to build out
 *                                           default advanced navigation settings
 *
 * @returns {Array} result  The ordered list of form sections.
 */
export function buildFormSections(form, options = {}) {

	const sections = form.form_sections || [];
	const fields = form.form_fields || [];

	let advancedSettings = [];

	// Initialize the advanced setting data for each section in the form
	if (options.tickets &&
		options.ticketGroups &&
		form.form_section_advanced_settings) {

		sections.forEach((section) => {

			advancedSettings = advancedSettings.concat(
				getAdvancedSettings(
					section.id,
					form.form_section_advanced_settings,
					options.tickets,
					options.ticketGroups
				)
			);

		});

	} else {

		advancedSettings = form.form_section_advanced_settings || [];

	}

	// Create a map of the form sections by "id" -> list of nested fields
	const fieldsBySectionID = {};

	// Create a map of the form sections by "id" -> list of advanced settings
	const settingsBySectionID = {};

	// Sort the form fields by weight
	const sortedFields = sortByWeight(fields);

	// Group the form fields into the appropriate form section
	sortedFields.forEach((field) => {

		if (!fieldsBySectionID[field.form_section_id]) {
			fieldsBySectionID[field.form_section_id] = [];
		}

		const sortedOptions = sortByWeight(field.form_field_options).map((option) => {

			return {
				id: option.id,
				value: option.value,
				is_correct_answer: option.is_correct_answer,
				continuation_type: option.continuation_type,
				continuation_section_id: option.continuation_section_id,
				weight: option.weight,
				form_field_id: option.form_field_id,
				form_field_option_contents: option.form_field_option_contents,
				deleted_form_field_option_contents: [],
			};

		});

		fieldsBySectionID[field.form_section_id].push({
			id: field.id,
			slug: field.slug,
			type: field.type,
			subtype: field.subtype,
			privacy: field.privacy,
			required: field.required,
			filter: field.filter,
			conditional: field.conditional,
			weight: field.weight,
			form_section_id: field.form_section_id,
			form_field_options: sortedOptions,
			form_field_contents: field.form_field_contents,
			deleted_form_field_contents: [],
		});

	});

	// Group the advance settings into the appropriate form section
	advancedSettings.forEach((setting) => {

		if (!settingsBySectionID[setting.form_section_id]) {
			settingsBySectionID[setting.form_section_id] = [];
		}

		settingsBySectionID[setting.form_section_id].push(setting);

	});

	// Build the final, ordered list of form sections with nested data
	const result = sortByWeight(sections).map((section) => {

		return {
			id: section.id,
			continuation_type: section.continuation_type,
			continuation_section_id: section.continuation_section_id,
			weight: section.weight,
			fields: fieldsBySectionID[section.id] || [],
			advanced_settings: settingsBySectionID[section.id] || [],
			form_section_contents: section.form_section_contents,
			deleted_form_section_contents: [],
		};

	});

	return result;

}

/**
 * Parses the ordered list of form items, i.e. fields and sections
 * to build the form fields and form order.
 *
 * @param {Array} formSections  The ordered list of form sections.
 *
 * @param {Object} options                  Function options.
 * @param {String} options.defaultLanguage  The default language for the form.
 *
 * @returns {Array} result
 * @returns {Array} result[0]  The list of form sections.
 * @returns {Array} result[1]  The list of form fields.
 * @returns {Array} result[2]  The list of form section advanced settings.
 */
export function parseFormSections(formSections, options = {}) {

	const defaultLanguage = options.defaultLanguage;

	const sections = [];
	const fields = [];
	const settings = [];

	// Loop through each section
	for (let i = 0, len = formSections.length; i < len; i++) {

		const section = formSections[i];

		// Add section data to list of form sections
		if (section.id) {

			const defaultSectionContent =
				section.form_section_contents.find((content) => content.language === defaultLanguage);

			sections.push({
				id: section.id,
				title: defaultSectionContent.title, // DEPRECATED
				description: defaultSectionContent.description, // DEPRECATED
				continuation_type: section.continuation_type,
				continuation_section_id: section.continuation_section_id,
				weight: section.weight,
				form_section_contents: section.form_section_contents,
				deleted_form_section_contents: section.deleted_form_section_contents,
			});

		}

		for (let j = 0, fieldsLen = section.fields.length; j < fieldsLen; j++) {

			const field = section.fields[j];

			const defaultFieldContent =
				field.form_field_contents.find((content) => content.language === defaultLanguage);

			const fieldData = {
				id: field.id,
				slug: field.slug,
				type: field.type,
				subtype: field.subtype,
				title: defaultFieldContent.title, // DEPRECATED
				help: defaultFieldContent.help, // DEPRECATED
				placeholder: defaultFieldContent.placeholder, // DEPRECATED
				submission_feedback_correct:
					defaultFieldContent.submission_feedback_correct, // DEPRECATED
				submission_feedback_incorrect:
					defaultFieldContent.submission_feedback_incorrect, // DEPRECATED
				required: field.required,
				privacy: field.privacy,
				filter: field.filter,
				conditional: field.conditional,
				weight: field.weight,
				form_section_id: section.id,
				form_field_options: field.form_field_options?.map((option) => {

					const defaultOptionContent =
						option.form_field_option_contents.find(
							(content) => content.language === defaultLanguage
						);

					return {
						id: option.id,
						label: defaultOptionContent.label, // DEPRECATED
						value: option.value,
						is_correct_answer: option.is_correct_answer,
						continuation_type: option.continuation_type,
						continuation_section_id: option.continuation_section_id,
						weight: option.weight,
						form_field_id: field.id,
						form_field_option_contents: option.form_field_option_contents,
						deleted_form_field_option_contents: option.deleted_form_field_option_contents,
					};

				}),
				form_field_contents: field.form_field_contents,
				deleted_form_field_contents: field.deleted_form_field_contents,
			};

			// If the field is missing a slug, we'll need to create a new one
			if (!fieldData.slug) {
				fieldData.slug = toSlug(fieldData.title || '', fieldData.id);
			}

			// Add the form field to the list of form fields
			fields.push(fieldData);

		}

		for (let j = 0, settingsLen = section.advanced_settings.length; j < settingsLen; j++) {

			const setting = section.advanced_settings[j];

			// Build out data to send to back-end
			const settingData = {
				id: setting.id,
				object_type: setting.object_type,
				object_id: setting.object_id,
				continuation_type: setting.continuation_type,
				continuation_section_id: setting.continuation_section_id,
				form_section_id: section.id,
			};

			// Add the advanced setting to the list of section settings
			settings.push(settingData);

		}

	}

	return [sections, fields, settings];

}

/**
 * Parses a form value to return the data,
 * with support for choosing the multi-language content based on the
 * options and the user's preferred language.
 *
 * @param {Object} formValue  The form value.
 * @param {Object} formField  The form field.
 * @param {Func} chooseContentFromLanguage  Callback to choose the translated content.
 *
 * @returns {Array|String} result  The parsed data for the form value.
 */
export function parseFormValueData(formValue, formField, chooseContentFromLanguage) {

	// Check if the form value has selected form field options
	const hasSelectedOptions = formValue?.selected_form_field_options?.length > 0;

	// If not, just use the initial data as is
	if (!hasSelectedOptions) {
		return formValue.data;
	}

	// Check if the form field supports selecting only a single option
	if (formField.type === FIELD_TYPES.RADIO || formField.type === FIELD_TYPES.SELECT) {

		// Use the selected option to find the up-to-date data value

		const selectedOptionId = formValue.selected_form_field_options[0].id;

		const selectedOption =
			formField.form_field_options?.find((option) => option.id === selectedOptionId);

		// Choose the translated content for the selected option in the user's preferred language

		const activeOptionContent = chooseContentFromLanguage(
			selectedOption?.form_field_option_contents
		);

		return (
			activeOptionContent?.label ||
			selectedOption?.value ||
			formValue.data
		);

	}

	// Otherwise, check if it supports selecting multiple options
	if (formField.type === FIELD_TYPES.CHECKBOXES) {

		// Use an array of the selected options to build up-to-date data value

		const selectedOptions =
			formValue.selected_form_field_options
				.map((sOption) => {

					const selectedOption =
						formField.form_field_options?.find((fOption) => fOption.id === sOption.id);

					return selectedOption;

				})
				.filter((option) => Boolean(option))
				.map((option) => {

					// Choose the translated content for the selected option in the user's preferred language

					const activeOptionContent = chooseContentFromLanguage(
						option.form_field_option_contents
					);

					return (
						activeOptionContent?.label ||
						option.value
					);

				});

		return selectedOptions;

	}

	// Otherwise, check if it supports selecting a single likert option
	if (formField.type === FIELD_TYPES.LIKERT) {

		// Use the selected option to find the up-to-date data value

		const selectedOptionId = formValue.selected_form_field_options[0].id;

		const selectedOption =
			formField.form_field_options?.find((option) => option.id === selectedOptionId);

		const parsedValue = selectedOption ? parseInt(selectedOption.value, 10) : null;

		// Choose the translated content for the selected option in the user's preferred language

		const activeOptionContent = chooseContentFromLanguage(
			selectedOption?.form_field_option_contents
		);

		// Check if the selected option has a custom label
		if (activeOptionContent?.label) {
			return `${parsedValue} - ${activeOptionContent.label}`;
		}

		// Otherwise, just use the likert value
		return parsedValue;

	}

	// Fallback to the initial data
	return formValue.data;

}

/**
 * Validates the input data for the form field.
 *
 * @param {object} field             The form field.
 * @param {bool|string|array} value  The input data for the form field.
 *
 * @returns {object} formValidation  The validation result, which includes any errors.
 */
export function validateFormField(field, value) {

	const errors = {};

	if (!field) {
		return errors;
	}

	// By default, reset the error state for the form field...
	errors[field.id] = false;
	errors[`${field.id}_msg`] = null;

	const type = field.type;
	const subtype = field.subtype;

	// Check if the form field is a required field
	if (field.required) {

		// Check if the input form field value is missing...
		const isMissing =
			!value ||
			(Array.isArray(value) && value.length === 0);

		if (isMissing) {
			errors[field.id] = true;
		}

	}

	// Check if the form field is a social media field
	if (type === FIELD_TYPES.SOCIAL_MEDIA) {

		if (SOCIAL_MEDIA_VALIDATION[subtype] && value) {

			const validation = SOCIAL_MEDIA_VALIDATION[subtype];

			// Split the value to remove the provided querystring (if any)
			const split = value.split('?');

			const valueToTest = split[0];

			// Test the value against the full expected url format
			const match1 = validation.urlRegex.test(valueToTest);

			// Test the value against only the username format
			const match2 = validation.usernameRegex.test(valueToTest);

			// Check if the social media input field value is invalid...
			if (!match1 && !match2) {

				errors[field.id] = true;
				errors[`${field.id}_msg`] =
					// eslint-disable-next-line max-len
					`Please provide your ${subtype} information in the format: ${validation.baseUrl}[username]`;

			}

			// Otherwise, it's good to go!

		}

	}

	return errors;

}

/**
 * Checks if the form is missing translations in the specified language.
 *
 * @param {object} form      The form.
 * @param {string} language  The language.
 *
 * @returns {boolean} result  True, if missing translations in the specified language.
 */
export function missingFormTranslations(form, language) {

	// Check if form content is translated in the language
	for (let i = 0, len = form.form_contents.length; i < len; i++) {

		if (form.form_contents[i].language === language) {
			return false;
		}

	}

	// Check if form section content is translated in the language
	for (let i = 0, len = form.form_sections.length; i < len; i++) {

		const section = form.form_sections[i];

		for (let j = 0, jLen = section.form_section_contents.length; j < jLen; j++) {

			if (section.form_section_contents[j].language === language) {
				return false;
			}

		}

	}

	// Check if form field content is translated in the language
	for (let i = 0, len = form.form_fields.length; i < len; i++) {

		const field = form.form_fields[i];

		for (let j = 0, jLen = field.form_field_contents.length; j < jLen; j++) {

			if (field.form_field_contents[j].language === language) {
				return false;
			}

		}

	}

	return true;

}
